import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { LoggerInstance } from '@vfit/shared/data-access';
import {
  CONSUMER_CMS_SHOPPING_CART,
  IGlobalConfigCMS,
  LOGGED_USER_KEYS,
  useCmsConfig,
  ICommonLabelsInStepper,
  organizeCommonLabelsInStepper,
} from '@vfit/consumer/data-access';
import { removeCurrency } from '@vfit/consumer/hooks';
import { IStepCard, IStepperUtils } from '@vfit/shared/models';
import { API, AppMobile } from '@vfit/shared/data-access';
import { ProductDetail, ISupportModule, StepperStateCards } from '@vfit/shared/components';
import { CheckoutModalContainer, OverlayGlobalStyle } from './checkoutEnergy.style';
import { getUserInfo, organizeFlowsAppend } from './checkoutEnergy.utils';
import { OTP_FLOW } from './checkoutEnergy.flow';
import { ID_FLOWS } from './checkoutEnergy.constants';
import { getLocalAddress } from '@vfit/consumer/ibuyfixed';
import { useEnergyCheckout } from '../energy.context';
import { ENERGY_FLOW } from './checkoutEnergy.flow';
import { ICheckoutEnergyModalProps } from './checkoutEnergy.models';

const CheckoutEnergy = ({
  handleClose,
  backgroundImage,
  isLoadingCart,
}: ICheckoutEnergyModalProps) => {
  const {
    isLastCard,
    findCaller,
    slidesGoBack,
    isDisabledBackButton,
    hideStickyBar,
    product,
    setSlidesGoBack,
    setIsModalButtonSticky,
  } = useEnergyCheckout();
  const queryClient = useQueryClient();
  const stepperRef = useRef<IStepperUtils>();
  const silentLogin: any = queryClient.getQueryData('silentLogin');
  const isLoggedUser = LOGGED_USER_KEYS.includes(silentLogin?.flowId) || false;
  const flow = ENERGY_FLOW.DEFAULT;
  const [steps, setSteps] = useState<IStepCard[]>(flow);

  const globalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const commonLabels = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_STEPPER_COMMON_LABELS_MOBILE
  ) as ICommonLabelsInStepper;

  const { goBackInStepper } = organizeCommonLabelsInStepper(commonLabels);

  const isButtonStickyConfig = globalConfig?.globalconfig?.modals?.enableButtonSticky === 'true';
  setIsModalButtonSticky(isButtonStickyConfig);

  // region Organize CMS
  const supportModulesCms = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_SUPPORT_MODULE
  ) as ISupportModule;

  // endregion

  // region Append steps

  const updateActiveIndexOnRemoveCards = (organizedStep: IStepCard[]) => {
    const diffSteps = steps.length - organizedStep.length;
    if (diffSteps > 0 && stepperRef.current) {
      const diff = stepperRef.current._getActiveSlideIndex() - diffSteps;
      stepperRef.current._setActiveSlideIndex(diff < 0 ? 0 : diff);
    }
  };

  const appendOtpAndPaymentSteps = () => {
    let organizedSteps = steps;
    const fromKeySteps = ID_FLOWS.PERSONAL_INFO;
    if (!findCaller.showOtp || AppMobile.checkIsApp()) {
      organizedSteps = organizeFlowsAppend(steps, false, fromKeySteps, OTP_FLOW.OTP);
    } else {
      organizedSteps = organizeFlowsAppend(
        steps,
        findCaller.showOtp || false,
        fromKeySteps,
        OTP_FLOW.OTP
      );
    }
    setSteps(organizedSteps);
    updateActiveIndexOnRemoveCards(organizedSteps);
  };

  // endregion

  /**
   * Method to go back programmatically from context
   */
  const programmaticallyGoBackNext = async (
    numberSlides: number,
    type: 'NEXT' | 'BACK',
    onFinish?: () => void
  ) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const delay = (time: number) => new Promise((resolve) => setTimeout(resolve, time));
    if (numberSlides !== -1) {
      // eslint-disable-next-line no-restricted-syntax
      for (const indexSlides of Array.from(Array(numberSlides - 1))) {
        LoggerInstance.debug('IndexSlides', indexSlides);
        if (type === 'NEXT') {
          stepperRef?.current?._stepperGoNext();
          await delay(100);
        } else {
          stepperRef?.current?._stepperGoPrev();
          await delay(600);
        }
      }
      // setSlidesGoBack(-1);
      setTimeout(() => {
        if (onFinish) {
          stepperRef?.current?._setActiveSlideIndex(numberSlides + 1);
          onFinish?.();
        }
      }, 150 * numberSlides);
    }
  };

  const handleRedirect = () => {
    LoggerInstance.debug('End flow');
  };

  useEffect(() => {
    appendOtpAndPaymentSteps();
  }, [findCaller]);

  useEffect(() => {
    const closeOnEscapeKey = (e: KeyboardEvent) => (e.key === 'Escape' ? handleClose() : null);
    document.body.addEventListener('keydown', closeOnEscapeKey);
    return () => {
      document.body.removeEventListener('keydown', closeOnEscapeKey);
    };
  }, [handleClose]);

  useEffect(() => {
    if (slidesGoBack && slidesGoBack !== -1 && stepperRef?.current) {
      programmaticallyGoBackNext(slidesGoBack, 'BACK');
      setSlidesGoBack(-1);
    }
  }, [slidesGoBack]);

  const getTitleStickyStepper = () => {
    if (!product?.recurrence) return product?.price;
    return `${product?.price}<span class="rec">/${product?.recurrence}</span>`;
  };

  const productToShow = product ? (
    <ProductDetail
      address={
        getLocalAddress()?.displayedAddress
          ? `<span data-cs-mask="">${getLocalAddress().displayedAddress}</span>`
          : ''
      }
      trackingOpt={{
        event: ['view'],
        event_label: 'offer details',
        opts: {
          product_target_segment: 'consumer',
          page_section: 'mobile offers',
          page_subsection: 'checkout',
        },
        cartProduct: {
          cart_product_id: `${product?.offerId}`,
          cart_product_name: product?.slug,
          cart_product_category: 'mobile',
          cart_product_price: product?.price && removeCurrency(product.price),
          cart_product_quantity: '1',
        },
        visitorTrackingOpts: getUserInfo(),
        disableInitialTrack: false,
      }}
      modalInfo={{
        title: supportModulesCms?.supportmodules?.needHelp?.title || '',
        ctaName: supportModulesCms?.supportmodules?.needHelp?.ctaSupportCall || '',
      }}
      details={{
        title: product.title,
        conditions: product.conditions,
        callMeNowHelp: product.callMeNowHelp,
        description: product.description,
        offerDetails: product.offerDetails,
        price: product.price,
        originalPrice: product.originalPrice,
        recurrence: product.recurrence,
        voucherPromoLabel: product?.voucherPromoLabel,
        voucherPromoMessage: product?.voucherPromoMessage,
        voucherPromoDetail: product?.voucherPromoDetail,
      }}
    />
  ) : (
    <div />
  );

  return (
    <>
      <OverlayGlobalStyle />
      <CheckoutModalContainer>
        <StepperStateCards
          ref={stepperRef}
          steps={steps}
          onBack={handleClose}
          onClose={handleClose}
          onFinish={handleRedirect}
          enableStickyStepper={!hideStickyBar}
          topLabelStickyStepper={product?.topLabelStickyCheckout || ''}
          titleStickyStepper={getTitleStickyStepper()}
          actionLabelStickyStepper={{
            openProductDetails: product?.buttonShow,
            closeProductDetails: product?.buttonHide,
          }}
          contentStickyStepper={productToShow}
          blurredImage={backgroundImage}
          forceFinish={isLastCard || false}
          hideBackButton={isDisabledBackButton}
          isLoadingContainer={!silentLogin || isLoggedUser ? isLoadingCart : false}
          isLoadingStickyOffer={isLoadingCart}
          lottieLoader={product?.loaders?.coverageTool}
          goBackLabel={goBackInStepper}
          showDetailLabel={product?.buttonShow || ''}
          hideDetailLabel={product?.buttonHide || ''}
        />
      </CheckoutModalContainer>
    </>
  );
};

export default CheckoutEnergy;
